import React, { useEffect, useState, useCallback } from 'react';
import {
  AnalyticalTable,
  Button,
  Dialog,
  Title,
  BusyIndicator,
  Input,
  Bar,
  Form,
  FormItem,
  FormGroup,
  MessageBox,
  MessageBoxType,
  MessageBoxAction,
  Text,
  Label,
  Select,
  Option,
  TextArea
} from '@ui5/webcomponents-react';
import axiosInstance from '../utils/axiosInstance';
import usePermission from '../hooks/usePermission';
import withPermission from './withPermission';
import useAutoTranslate from '../hooks/useAutoTranslate';

const EMPTY_FORM = {
  shipCode: '',
  shipName: '',
  country: '',
  shippingCost: '',
  trackingServiceURL: '',
  trackingServicesPayload: '',
  shipExternalId: '',
  chargingMethod: '',
  typeTransport: ''
};

const CHARGING_METHODS = ['STANDARD', 'EXPRESS', 'PICKUP', 'FREIGHT'];
const TYPE_TRANSPORTS = ['LAND', 'AIR', 'SEA', 'MULTIMODAL'];

const ShippingMethodList = () => {
  const { hasPermission } = usePermission();
  const { autoT } = useAutoTranslate();

  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ sortBy: 'shipCode', sortDirection: 'asc' });

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedShippingMethod, setSelectedShippingMethod] = useState(null);

  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});

  const fetchShippingMethods = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(`shipping-methods?page=${page}&size=${pageSize}&sortBy=shipCode`);
      if (response.data.records.code === 'SUCCESS') {
        setData(response.data.records.data || []);
        setTotalPages(Math.max(response.data.totalCount || 1, 1));
      } else {
        setError(response.data.records.message);
      }
    } catch (err) {
      setError(autoT('Error al cargar los Métodos de Envío'));
    } finally {
      setLoading(false);
    }
  }, [pageSize]);

  useEffect(() => { fetchShippingMethods(currentPage); }, [currentPage, fetchShippingMethods]);

  useEffect(() => {
    let filtered = [...data];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(method =>
        (method.shipCode || '').toLowerCase().includes(term) ||
        (method.shipName || '').toLowerCase().includes(term) ||
        (method.country || '').toLowerCase().includes(term) ||
        (method.shipExternalId || '').toLowerCase().includes(term) ||
        (method.chargingMethod || '').toLowerCase().includes(term) ||
        (method.typeTransport || '').toLowerCase().includes(term)
      );
    }

    filtered.sort((a, b) => {
      const valueA = a[sortConfig.sortBy] ?? '';
      const valueB = b[sortConfig.sortBy] ?? '';
      const compare = typeof valueA === 'number' || typeof valueB === 'number'
        ? Number(valueA || 0) - Number(valueB || 0)
        : String(valueA).localeCompare(String(valueB));

      return sortConfig.sortDirection === 'asc' ? compare : -compare;
    });

    setFilteredData(filtered);
  }, [data, searchTerm, sortConfig]);

  const handleSort = (column) =>
    setSortConfig(prev => ({
      sortBy: column,
      sortDirection: prev.sortBy === column && prev.sortDirection === 'asc' ? 'desc' : 'asc'
    }));

  const handlePrevPage = () => { if (currentPage > 1) setCurrentPage(page => page - 1); };
  const handleNextPage = () => { if (currentPage < totalPages) setCurrentPage(page => page + 1); };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSelectChange = (event) => {
    const { name } = event.target;
    const value = event.detail.selectedOption.getAttribute('value');
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: '' }));
  };

  const buildPayload = () => ({
    shipCode: formData.shipCode.trim(),
    shipName: formData.shipName.trim(),
    country: formData.country.trim() || null,
    shippingCost: formData.shippingCost === '' ? null : Number(formData.shippingCost),
    trackingServiceURL: formData.trackingServiceURL.trim() || null,
    trackingServicesPayload: formData.trackingServicesPayload.trim() || null,
    shipExternalId: formData.shipExternalId.trim() || null,
    chargingMethod: formData.chargingMethod || null,
    typeTransport: formData.typeTransport || null
  });

  const validateForm = (isEdit = false) => {
    const errors = {};
    if (!isEdit && (!formData.shipCode || formData.shipCode.trim() === '')) {
      errors.shipCode = autoT('El código es obligatorio');
    }
    if (!formData.shipName || formData.shipName.trim() === '') {
      errors.shipName = autoT('El nombre es obligatorio');
    }
    if (formData.shippingCost !== '') {
      const cost = Number(formData.shippingCost);
      if (!Number.isFinite(cost) || cost < 0) {
        errors.shippingCost = autoT('El costo debe ser un número mayor o igual a cero');
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const openCreateDialog = () => {
    if (!hasPermission('shippingmethod', 'create')) return;
    setFormData(EMPTY_FORM);
    setFormErrors({});
    setShowCreateDialog(true);
  };

  const fillForm = (method) => {
    setSelectedShippingMethod(method);
    setFormData({
      shipCode: method.shipCode || '',
      shipName: method.shipName || '',
      country: method.country || '',
      shippingCost: method.shippingCost ?? '',
      trackingServiceURL: method.trackingServiceURL || '',
      trackingServicesPayload: method.trackingServicesPayload || '',
      shipExternalId: method.shipExternalId || '',
      chargingMethod: method.chargingMethod || '',
      typeTransport: method.typeTransport || ''
    });
    setFormErrors({});
  };

  const handleRowClick = (event) => {
    if (!hasPermission('shippingmethod', 'update')) return;
    const method = event.detail?.row?.original;
    if (method) {
      fillForm(method);
      setShowEditDialog(true);
    }
  };

  const handleEditClick = (method) => {
    if (!hasPermission('shippingmethod', 'update')) return;
    fillForm(method);
    setShowEditDialog(true);
  };

  const handleCreateSubmit = async () => {
    if (!validateForm(false)) return;
    try {
      const response = await axiosInstance.post('shipping-methods', buildPayload());
      if (response.status === 200 || response.status === 201) {
        fetchShippingMethods(currentPage);
        setShowCreateDialog(false);
      } else {
        setError(autoT('Error al crear el Método de Envío'));
      }
    } catch (err) {
      const backendMsg = err.response?.data?.message;
      setError(backendMsg && backendMsg !== 'Record Not Created'
        ? backendMsg
        : autoT('Error al crear el Método de Envío'));
    }
  };

  const handleEditSubmit = async () => {
    if (!validateForm(true) || !selectedShippingMethod) return;
    try {
      const response = await axiosInstance.put(
        `shipping-methods/${encodeURIComponent(selectedShippingMethod.shipCode)}`,
        buildPayload()
      );
      if (response.status === 200) {
        fetchShippingMethods(currentPage);
        setShowEditDialog(false);
      } else {
        setError(autoT('Error al actualizar el Método de Envío'));
      }
    } catch (err) {
      const backendMsg = err.response?.data?.message;
      setError(backendMsg && backendMsg !== 'Record Not Updated'
        ? backendMsg
        : autoT('Error al actualizar el Método de Envío'));
    }
  };

  const handleDeleteClick = (method) => {
    if (!hasPermission('shippingmethod', 'delete')) return;
    setSelectedShippingMethod(method || selectedShippingMethod);
    setShowDeleteConfirm(true);
    if (showEditDialog) setShowEditDialog(false);
  };

  const handleConfirmDelete = async () => {
    if (!selectedShippingMethod) return;
    try {
      await axiosInstance.delete(`shipping-methods/${encodeURIComponent(selectedShippingMethod.shipCode)}`);
      fetchShippingMethods(currentPage);
      setShowDeleteConfirm(false);
    } catch (err) {
      const backendMsg = err.response?.data?.message;
      setError(backendMsg && backendMsg !== 'Record Not Deleted'
        ? backendMsg
        : autoT('Error al eliminar el Método de Envío'));
    }
  };

  const columns = [
    {
      Header: autoT('Código'),
      accessor: 'shipCode',
      headerProps: { onClick: () => handleSort('shipCode') },
      className: sortConfig.sortBy === 'shipCode' ? `sorted-${sortConfig.sortDirection}` : ''
    },
    {
      Header: autoT('Nombre'),
      accessor: 'shipName',
      headerProps: { onClick: () => handleSort('shipName') },
      className: sortConfig.sortBy === 'shipName' ? `sorted-${sortConfig.sortDirection}` : ''
    },
    {
      Header: autoT('País'),
      accessor: 'country',
      headerProps: { onClick: () => handleSort('country') },
      Cell: ({ value }) => <Text>{value || '-'}</Text>
    },
    {
      Header: autoT('Costo'),
      accessor: 'shippingCost',
      headerProps: { onClick: () => handleSort('shippingCost') },
      Cell: ({ value }) => <Text>{value ?? '-'}</Text>
    },
    {
      Header: autoT('Cobro'),
      accessor: 'chargingMethod',
      headerProps: { onClick: () => handleSort('chargingMethod') },
      Cell: ({ value }) => <Text>{value || '-'}</Text>
    },
    {
      Header: autoT('Transporte'),
      accessor: 'typeTransport',
      headerProps: { onClick: () => handleSort('typeTransport') },
      Cell: ({ value }) => <Text>{value || '-'}</Text>
    },
    {
      Header: autoT('Acciones'),
      id: 'actions',
      Cell: ({ row }) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          {hasPermission('shippingmethod', 'update') && (
            <Button
              icon="edit"
              design="Transparent"
              onClick={(event) => { event.stopPropagation(); handleEditClick(row.original); }}
            />
          )}
          {hasPermission('shippingmethod', 'delete') && (
            <Button
              icon="delete"
              design="Transparent"
              onClick={(event) => { event.stopPropagation(); handleDeleteClick(row.original); }}
            />
          )}
        </div>
      )
    }
  ];

  const renderForm = (isEdit = false) => (
    <Form
      style={{ padding: '1rem' }}
      backgroundDesign="Transparent"
      columnsXL={1}
      columnsL={1}
      columnsM={1}
      columnsS={1}
      labelSpanXL={4}
      labelSpanL={4}
      labelSpanM={12}
      labelSpanS={12}
    >
      <FormGroup titleText={autoT('Información del Método de Envío')}>
        <FormItem labelContent={<Label required>{autoT('Código')}</Label>}>
          <Input
            name="shipCode"
            value={formData.shipCode}
            onChange={handleInputChange}
            valueState={formErrors.shipCode ? 'Error' : 'None'}
            valueStateMessage={formErrors.shipCode}
            disabled={isEdit}
            required
            style={{ width: '100%' }}
          />
        </FormItem>
        <FormItem labelContent={<Label required>{autoT('Nombre')}</Label>}>
          <Input
            name="shipName"
            value={formData.shipName}
            onChange={handleInputChange}
            valueState={formErrors.shipName ? 'Error' : 'None'}
            valueStateMessage={formErrors.shipName}
            required
            style={{ width: '100%' }}
          />
        </FormItem>
        <FormItem labelContent={<Label>{autoT('País')}</Label>}>
          <Input
            name="country"
            value={formData.country}
            onChange={handleInputChange}
            style={{ width: '100%' }}
          />
        </FormItem>
        <FormItem labelContent={<Label>{autoT('Costo de Envío')}</Label>}>
          <Input
            type="Number"
            name="shippingCost"
            value={formData.shippingCost}
            onChange={handleInputChange}
            valueState={formErrors.shippingCost ? 'Error' : 'None'}
            valueStateMessage={formErrors.shippingCost}
            style={{ width: '100%' }}
          />
        </FormItem>
        <FormItem labelContent={<Label>{autoT('Método de Cobro')}</Label>}>
          <Select name="chargingMethod" onChange={handleSelectChange} value={formData.chargingMethod}>
            <Option value="">{autoT('Seleccione método de cobro')}</Option>
            {CHARGING_METHODS.map(method => (
              <Option key={method} value={method}>{method}</Option>
            ))}
          </Select>
        </FormItem>
        <FormItem labelContent={<Label>{autoT('Tipo de Transporte')}</Label>}>
          <Select name="typeTransport" onChange={handleSelectChange} value={formData.typeTransport}>
            <Option value="">{autoT('Seleccione tipo de transporte')}</Option>
            {TYPE_TRANSPORTS.map(type => (
              <Option key={type} value={type}>{type}</Option>
            ))}
          </Select>
        </FormItem>
        <FormItem labelContent={<Label>{autoT('ID Externo')}</Label>}>
          <Input
            name="shipExternalId"
            value={formData.shipExternalId}
            onChange={handleInputChange}
            style={{ width: '100%' }}
          />
        </FormItem>
        <FormItem labelContent={<Label>{autoT('URL Tracking')}</Label>}>
          <Input
            name="trackingServiceURL"
            value={formData.trackingServiceURL}
            onChange={handleInputChange}
            style={{ width: '100%' }}
          />
        </FormItem>
        <FormItem labelContent={<Label>{autoT('Payload Tracking')}</Label>}>
          <TextArea
            name="trackingServicesPayload"
            value={formData.trackingServicesPayload}
            onInput={handleInputChange}
            rows={4}
            style={{ width: '100%' }}
          />
        </FormItem>
      </FormGroup>
    </Form>
  );

  return (
    <div style={{ padding: '1.5rem', backgroundColor: 'var(--sapBackgroundColor)', minHeight: '100vh' }}>
      <div style={{ padding: '1rem' }}>
        <Title level="H2">{autoT('Gestión de Métodos de Envío')}</Title>

        {error && <div style={{ color: 'red', margin: '1rem 0' }}>{error}</div>}

        <Bar
          startContent={
            <Input
              placeholder={autoT('Buscar método de envío...')}
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              style={{ width: '250px' }}
            />
          }
          endContent={
            hasPermission('shippingmethod', 'create') && (
              <Button icon="add" design="Emphasized" onClick={openCreateDialog}>
                {autoT('Nuevo Método de Envío')}
              </Button>
            )
          }
        />

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
            <BusyIndicator active size="Large" />
          </div>
        ) : (
          <>
            <AnalyticalTable
              data={filteredData}
              columns={columns}
              selectionMode="None"
              onRowClick={hasPermission('shippingmethod', 'update') ? handleRowClick : undefined}
              style={{ marginTop: '1rem' }}
              visibleRows={10}
              minRows={10}
              alternateRowColor
            />
            <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginTop: '1rem' }}>
              <Button icon="navigation-left-arrow" disabled={currentPage === 1} onClick={handlePrevPage}>
                {autoT('Anterior')}
              </Button>
              <Text style={{ alignSelf: 'center' }}>
                {autoT('Página')} {currentPage} {autoT('de')} {totalPages}
              </Text>
              <Button icon="navigation-right-arrow" disabled={currentPage === totalPages} onClick={handleNextPage} iconEnd>
                {autoT('Siguiente')}
              </Button>
            </div>
          </>
        )}
      </div>

      <Dialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        header={<Bar startContent={<Title level="H4">{autoT('Crear Método de Envío')}</Title>} />}
        footer={
          <Bar endContent={
            <>
              <Button design="Emphasized" onClick={handleCreateSubmit}>{autoT('Guardar')}</Button>
              <Button design="Transparent" onClick={() => setShowCreateDialog(false)}>{autoT('Cancelar')}</Button>
            </>
          } />
        }
        style={{ width: '760px' }}
      >
        {renderForm(false)}
      </Dialog>

      <Dialog
        open={showEditDialog}
        onClose={() => setShowEditDialog(false)}
        header={<Bar startContent={<Title level="H4">{autoT('Editar Método de Envío')}: {selectedShippingMethod?.shipName}</Title>} />}
        footer={
          <Bar
            startContent={
              hasPermission('shippingmethod', 'delete') && (
                <Button design="Negative" icon="delete" onClick={() => handleDeleteClick(null)}>
                  {autoT('Eliminar')}
                </Button>
              )
            }
            endContent={
              <>
                <Button design="Emphasized" onClick={handleEditSubmit}>{autoT('Guardar Cambios')}</Button>
                <Button design="Transparent" onClick={() => setShowEditDialog(false)}>{autoT('Cancelar')}</Button>
              </>
            }
          />
        }
        style={{ width: '760px' }}
      >
        {renderForm(true)}
      </Dialog>

      <MessageBox
        open={showDeleteConfirm}
        onClose={(event) => {
          if (event.detail.action === MessageBoxAction.OK) {
            handleConfirmDelete();
          } else {
            setShowDeleteConfirm(false);
          }
        }}
        title={autoT('Confirmar eliminación')}
        type={MessageBoxType.WARNING}
        actions={[MessageBoxAction.OK, MessageBoxAction.CANCEL]}
      >
        {autoT('¿Está seguro que desea eliminar el método de envío')} <strong>{selectedShippingMethod?.shipName}</strong>? {autoT('Esta acción no se puede deshacer.')}
      </MessageBox>
    </div>
  );
};

export default withPermission(ShippingMethodList, 'shippingmethod', 'listView');
